/**
 * The Przegląd tab's main column — KANON „Karty zadań", layout A (`docs/design/karty-zadan-2026-09-24/`,
 * `canvas.js`), owner, 24 September 2026. Two sections, the tasks always first:
 *
 * 1. **„Wymagają reakcji · N"** — this client's open tasks from the same queue the Pulpit and
 *    `/zadania` read, as the Talia: ONE quick card at a time, one height for every kind, with the
 *    tabs of all of them above it. Tabs from two tasks up; one task is its card alone; none is no
 *    section at all, only a line that says the trainer is up to date (`taliaLayout`).
 * 2. **„Ostatnie sesje"** — the table: eight rows, missed plan days woven in by date, Δ tonnage
 *    against the previous run of the same session (`model/sessionTable`).
 *
 * ── The tasks are BACK on this screen, and that reverses 1 September 2026 ─────────────────────
 * The owner took the tasks card off Przegląd on 1 September 2026 („open items stay reachable
 * through the Inbox"), and the two hand-drawn „now" cards that replaced it answered one kind of
 * task each. On 24 September 2026 the owner reversed that: every kind of task this client has is
 * drawn here, with the very card `/zadania` draws, so a task is answered where the client is being
 * read. No side panel and no „Szczegóły" — the card is the whole of it.
 *
 * ── One wait for the column ──────────────────────────────────────────────────────────────────
 * Whether the task section is a Talia, one card or one line decides where the table stands, so the
 * column waits for all four of its reads (`lib/useOverview`) and is handed over whole, on
 * `gate.reveal`. The queue is usually warm from the Pulpit, so the common case draws no wait at all.
 *
 * ── Above both, the „Rozliczenie" band (booking wave 4, T-K1) ──────────────────────────────────
 * Drawn only while the trainer bills in credits; its reads join the column's one gate, and it
 * holds its room on the booking policy the route read before the first frame (`CreditBand`). The
 * tab speaks through the card's ONE live region (`cardAnnouncer`), the tasks' answers included.
 */

import type { ReactNode } from 'react';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';

import { useWellbeingLevels } from '~/entities/client';
import {
  MutedNote,
  primaryStandsInFoot,
  SessionLink,
  SurveyLink,
  surveyRead,
  taskLabel,
  TaskControls,
  TaskTaliaCard,
  TaskTaliaTabs,
  taliaLayout,
  useHomeFormats,
  type AttentionItem,
  type TaskSlots,
} from '~/features/attention-queue';
import { SurveyTaskReading } from '~/features/survey-report';
import { screenMotion } from '~/shared/design-system/motion';
import { sessionOpenerKey, type SessionOpenIntent } from '~/shared/lib/sessionOpener';
import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import { EmptyState } from '~/shared/ui/EmptyState';
import ghost from '~/shared/ui/ghostButton.module.css';
import { DoneMarkIcon, ElapsedMarkIcon } from '~/shared/ui/EmptyStateIcons';
import skel from '~/shared/ui/skeleton.module.css';
import stateAction from '~/shared/ui/stateAction.module.css';
import { tabButtonId, tabPanelId } from '~/shared/ui/TabStrip';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import { useCreditBand } from '../lib/useCreditBand';
import { useOverview } from '../lib/useOverview';
import { useOverviewTasks } from '../lib/useOverviewTasks';
import { PROGRESS_WINDOW_DAYS } from '../model/sessionTable';
import { upToDateHint } from '../model/upToDate';
import { useCardAnnounce } from './cardAnnouncer';
import { CreditBand } from './CreditBand';
import { SectionHead } from './SectionHead';
import { SessionTable } from './SessionTable';
import styles from './OverviewTab.module.css';

interface OverviewTabProps {
  readonly clientId: number;
  /** The client's full name — the up-to-date line says whom nothing is waiting on. */
  readonly clientName: string;
  readonly clientInitials: string;
  /**
   * Open one session. `comment` additionally puts focus in the composer, and is used by exactly one
   * control: the session task's „Skomentuj sesję". A table row opened to READ a session keeps focus
   * on the panel heading.
   */
  readonly onOpenSession: (workoutExecutionId: number, intent: SessionOpenIntent) => void;
  /** „Przypisz następny" — the screen's own new-plan dialog, built for this client. */
  readonly onAssignPlan: () => void;
  /**
   * Open the screen's one player on a clip, over this tab. The queue's own `clipDestination` would
   * drop the roster list this card was opened from and close the player onto Forma.
   */
  readonly onOpenClip: (clipId: number) => void;
  /** A survey task's „Przejrzyj": this screen's Ankiety, on the submitted occurrence. */
  readonly onOpenSurvey: (surveyAssignmentId: number) => void;
  /** Open the client's Kalendarz tab — the „Rozliczenie" band's „Kalendarz klienta". */
  readonly onOpenCalendar: () => void;
  readonly reducedMotion: boolean;
}

/** How many skeleton rows stand in for the table while the column waits. */
const WAIT_ROWS = 4;

export function OverviewTab({
  clientId,
  clientName,
  clientInitials,
  onOpenSession,
  onAssignPlan,
  onOpenClip,
  onOpenSurvey,
  onOpenCalendar,
  reducedMotion,
}: OverviewTabProps) {
  const { t } = useTranslation();
  const formats = useHomeFormats();
  const overview = useOverview(clientId);
  const announce = useCardAnnounce();
  const tasks = useOverviewTasks(overview.tasks, overview.tasksUpdatedAt, announce);
  const band = useCreditBand(clientId, announce);
  // "Low" on the session table's sleep and energy bars is the trainer's own level (attention-queue
  // R25). Read here, beside the tab's other reads, and waited for with them.
  const wellbeing = useWellbeingLevels();
  // One gate for the tab: the column's four reads, the band's first reads and the levels wait
  // together, so none can flash a skeleton the others are not showing — and no bar is drawn under
  // one level and repainted under another.
  const gate = useSkeletonGate(overview.pending || band.pending || wellbeing.pending);

  return (
    <div className={styles.tab}>
      <CreditBand
        clientId={clientId}
        clientName={clientName}
        band={band}
        gate={gate}
        onOpenCalendar={onOpenCalendar}
        reducedMotion={reducedMotion}
      />

      {/* One box stands from the first frame and enters once; the answer is handed over INSIDE it. */}
      <motion.div
        className={styles.column}
        aria-busy={gate.waiting}
        {...screenMotion('rise', { reducedMotion, index: 0 })}
      >
        {gate.waiting ? (
          <div className={styles.wait}>
            {gate.skeleton ? (
              <>
                <i className={`${String(skel.bar)} ${String(styles.skelHead)}`} />
                <span className={styles.skelCard}>
                  <i className={`${String(skel.bar)} ${String(styles.skelLine)}`} />
                  <i className={`${String(skel.bar)} ${String(styles.skelFigure)}`} />
                  <i className={`${String(skel.bar)} ${String(styles.skelLineShort)}`} />
                </span>
                <i className={`${String(skel.bar)} ${String(styles.skelHead)}`} />
                <span className={styles.skelTable}>
                  {Array.from({ length: WAIT_ROWS }, (_, row) => (
                    <i key={row} className={`${String(skel.bar)} ${String(styles.skelRow)}`} />
                  ))}
                </span>
              </>
            ) : null}
            {/* LAST, so a measurement keyed by position pairs a bar with its counterpart. */}
            <span className={hidden.srOnly}>{t('web:clientDetail.states.loading')}</span>
          </div>
        ) : (
          <div className={`${String(styles.loaded)} ${gate.reveal}`}>
            {taskSection()}
            <section className={styles.section} aria-label={t('web:clientDetail.sessions.heading')}>
              <SectionHead
                title={t('web:clientDetail.sessions.heading')}
                aside={sessionsAside()}
                reducedMotion={reducedMotion}
                enters={false}
              />
              {/* The failure FIRST: the missed plan days alone are not a shorter table (`useOverview`). */}
              {overview.sessionsFailed ? (
                failure(t('web:clientDetail.sessions.failed'), overview.retrySessions)
              ) : overview.sessions.length > 0 ? (
                <SessionTable
                  rows={overview.sessions}
                  clientInitials={clientInitials}
                  levels={wellbeing.levels}
                  onOpenSession={onOpenSession}
                />
              ) : (
                // NEUTRAL: a client with no finished session states a fact and asks for nothing.
                <EmptyState
                  className={styles.emptyCard}
                  mark={<ElapsedMarkIcon />}
                  tone="neutral"
                  title={t('web:clientDetail.sessions.empty')}
                />
              )}
            </section>
          </div>
        )}
      </motion.div>
    </div>
  );

  function taskSection() {
    const layout = taliaLayout(overview.tasks.length);
    const item = tasks.selected;

    if (layout === 'none' || item === null) {
      // A failed queue is not „nothing waiting": the line says so instead of claiming the clear.
      if (overview.tasksFailed) {
        return failure(t('web:clientDetail.tasks.failed'), overview.retryTasks);
      }
      // POSITIVE, and one line tall where the section would stand — the owner's „Jesteś na
      // bieżąco", never an empty card. A muted kind is named rather than „nic nie czeka" (R18).
      const hint = upToDateHint(clientName, overview.mutedReasons, (key, values) => t(key, values));
      return (
        // The wrapper is where the keyboard lands when the last task is answered (`useOverviewTasks`).
        <div ref={tasks.upToDateRef} tabIndex={-1}>
          <EmptyState
            inline
            className={styles.upToDate}
            mark={<DoneMarkIcon />}
            tone="positive"
            title={
              // An inline state carries no hint of its own, so the quieter half rides on the title's
              // line — the canon's „Jesteś na bieżąco · nic nie czeka na Twoją reakcję".
              <>
                {t('web:tasks.talia.upToDate')}{' '}
                <span className={styles.upToDateHint}>
                  {hint.kind === 'muted' ? <MutedNote sentence={hint.text} /> : hint.text}
                </span>
              </>
            }
          />
        </div>
      );
    }

    return (
      <section className={styles.section} aria-label={t('web:tasks.talia.heading')}>
        <SectionHead
          title={t('web:tasks.talia.heading')}
          count={overview.tasks.length}
          reducedMotion={reducedMotion}
          enters={false}
        />
        {layout === 'tabs' ? (
          <TaskTaliaTabs
            items={overview.tasks}
            selectedKey={item.key}
            onSelect={tasks.select}
            now={overview.now}
          />
        ) : null}
        <TaskTaliaCard
          key={item.key}
          item={item}
          variant="quick"
          now={overview.now}
          age={item.signalAtUtc === null ? '' : formats.shortDate(item.signalAtUtc)}
          slots={slots()}
          reducedMotion={reducedMotion}
          headingRef={tasks.headingRef}
          panel={
            layout === 'tabs'
              ? { id: tabPanelId(item.key), labelledBy: tabButtonId(item.key) }
              : undefined
          }
          foot={foot(item)}
        />
      </section>
    );
  }

  /**
   * A read that failed, said in one line, and the retry under it. The press puts the column back in
   * its wait (`useOverview` counts an errored read that is fetching as pending), so the button needs
   * no busy state of its own.
   */
  function failure(sentence: string, retry: () => void) {
    return (
      <div className={styles.failure}>
        <p className={styles.notice}>{sentence}</p>
        <button
          type="button"
          className={`${String(ghost.ghost)} ${String(stateAction.stateAction)}`}
          onClick={retry}
        >
          {t('web:clientDetail.states.retry')}
        </button>
      </div>
    );
  }

  function slots(): TaskSlots {
    return {
      // No `note`: the quick card writes no note in place, the session's own primary opens its panel.
      // No `onResolved`: the queue's next read is the close here; `useOverviewTasks` owns where the
      // keyboard goes.
      writes: tasks.writes,
      survey: (survey, density) => (
        <SurveyTaskReading {...survey} density={density} reducedMotion={reducedMotion} />
      ),
      assignNext: onAssignPlan,
      openClip: onOpenClip,
      announce: tasks.announce,
    };
  }

  function foot(item: AttentionItem) {
    const action = item.action;
    return (
      <TaskControls
        item={item}
        expanded={false}
        spread
        primary={footPrimary(item)}
        beside={beside(item)}
        onPrimary={() => {
          if (action.kind === 'markReviewed') tasks.markReviewed(item, action.surveyAssignmentId);
        }}
        onAnswer={(kind) => {
          tasks.answer(item, kind);
        }}
      />
    );
  }

  /**
   * The foot's READ beside the primary: „Przejrzyj" on a survey and „Zobacz check-in" on a low
   * reading a survey reported (this screen's Ankiety), „Zobacz sesję" where the primary is not
   * already the way into that session (the session task's „Skomentuj sesję" is).
   */
  function beside(item: AttentionItem): ReactNode {
    if (surveyRead(item) !== null) return <SurveyLink item={item} onOpen={onOpenSurvey} />;
    if (item.action.kind === 'sessionNote') return null;
    return (
      <SessionLink
        item={item}
        glyph
        onOpen={(destination) => {
          onOpenSession(destination.workoutExecutionId, 'read');
        }}
      />
    );
  }

  /**
   * The foot's primary, where this surface draws its own: none for a kind whose act is inside the
   * card, „Skomentuj sesję" as the way into the session panel, „Obejrzyj" as the way into this
   * screen's player, and the house primary otherwise.
   */
  function footPrimary(item: AttentionItem): ((skin: string, glyph: ReactNode) => ReactNode) | undefined {
    const action = item.action;
    if (!primaryStandsInFoot(action, 'quick')) return () => null;
    if (action.kind === 'openClip') {
      return (skin, glyph) => (
        <button
          type="button"
          className={skin}
          // The same name the house link carries: its word, then the person.
          aria-label={t('web:home.action.forTask', { action: t(action.labelKey), task: taskLabel(item, t) })}
          onClick={() => {
            onOpenClip(action.clipId);
          }}
        >
          {glyph}
          {t(action.labelKey)}
        </button>
      );
    }
    if (action.kind !== 'sessionNote') return undefined;
    return (skin, glyph) => (
      <button
        type="button"
        className={skin}
        // The address the screen focuses back to when the session panel closes; the INTENT is part
        // of it because the table below opens the same session to read.
        data-session-opener={sessionOpenerKey(action.workoutExecutionId, 'comment')}
        onClick={() => {
          onOpenSession(action.workoutExecutionId, 'comment');
        }}
      >
        {glyph}
        {t('web:tasks.talia.comment')}
      </button>
    );
  }

  function sessionsAside(): string {
    const summary = overview.summary;
    if (summary === null) return t('web:clientDetail.sessions.asideFallback');
    return t('web:clientDetail.sessions.aside', {
      sessions: t('web:clientDetail.sessions.sessions', { count: summary.sessions }),
      records: t('web:clientDetail.sessions.records', { count: summary.personalRecords }),
      days: PROGRESS_WINDOW_DAYS,
    });
  }
}
